import { useEffect, useState, useRef, useCallback } from 'react';
import type { RootState } from '../redux/store';
import { store } from '../redux/store';

type Predicate = (state: RootState) => boolean;

export const useStoreReady = (predicate?: Predicate) => {
  const [ready, setReady] = useState(false);
  const unsubscribeRef = useRef<(() => void) | undefined>(undefined);

  const checkReady = useCallback(() => {
    if (!store) return false;
    const state = store.getState();

    if (predicate) {
      return predicate(state);
    }

    return !!state.StartAppSettings?.feature_flags;
  }, [predicate]);

  useEffect(() => {
    if (checkReady()) {
      setReady(true);

      return;
    }

    unsubscribeRef.current = store.subscribe(() => {
      if (checkReady()) {
        setReady(true);

        if (unsubscribeRef.current) unsubscribeRef.current();
      }
    });

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [checkReady]);

  const waitForStore = useCallback(
    () =>
      new Promise<void>(resolve => {
        if (checkReady()) {
          resolve();

          return;
        }
        const unsubscribe = store.subscribe(() => {
          if (checkReady()) {
            unsubscribe();
            resolve();
          }
        });
      }),
    [checkReady],
  );

  return { isStoreReady: ready, waitForStore };
};
